// libraries/fe-logger/src/index.tsx

import { FeLoggerClient } from "./client";

export type FeLoggerAdditionalData = Partial<{
  app: string;
  homepage: string;
  mt_language: string;
  mt_pathname: string;
  mt_render_mode: string;
  mt_route_group: string;
  mt_variant: string;
  trustArc: string;
}>;
export type FeLoggerConfig = {
  additionalData?: FeLoggerAdditionalData;
  applicationInfo?: { id: string; version: string };
  userId?: string;
  sessionId?: string;
  loggerSettings: LoggerSettings;
  loggerUrl?: string;
  localStorageId?: string;
  environment?: EnvironmentNames;
  subscriptionKey: string;
};

type LoggerSettings = {
  capturePageLoad: boolean;
  captureHrefChange: boolean;
  captureError: boolean;
  captureClick: boolean;
  enableLoggingToService?: boolean;
  enableConsoleLogs?: boolean;
  batchMessages?: boolean;
};

type EnvironmentNames = "local-dev" | "dev" | "stg" | "qa" | "prd";

declare const addAddtionalData:
  | ((config: Partial<FeLoggerConfig>) => void)
  | undefined;
declare const JBFELoggerSetup: ((config: FeLoggerConfig) => void) | undefined;
declare const logOtherEvent:
  | ((actionType: string, additionalProperties: unknown) => void)
  | undefined;

const getAddAdditionalData = () =>
  typeof addAddtionalData === "function" ? addAddtionalData : undefined;

const getJBFELoggerSetup = () =>
  typeof JBFELoggerSetup === "function" ? JBFELoggerSetup : undefined;

const getLogOtherEvent = () =>
  typeof logOtherEvent === "function" ? logOtherEvent : undefined;

export type FeLoggerProperties = {
  src: string;
  subscriptionKey: string;
};
export const FeLogger = ({ src, subscriptionKey }: FeLoggerProperties) => (
  <FeLoggerClient src={src} subscriptionKey={subscriptionKey} />
);

let resolveLoad: () => void;

const loaded = new Promise<void>((resolve) => {
  resolveLoad = resolve;
});

export type InitializeLoggerProperties = Omit<
  FeLoggerConfig,
  "loggerSettings"
> &
  Partial<Pick<FeLoggerConfig, "loggerSettings">>;
export const initializeLogger =
  ({ loggerSettings, ...properties }: InitializeLoggerProperties) =>
  () => {
    void logData({ pageViewEvent: "onload" }, "page_view");

    getJBFELoggerSetup()?.({
      ...properties,
      applicationInfo: {
        id: "ManageTripsNext",
        version:
          process.env.NEXT_PUBLIC_GITHUB_SHA ??
          process.env.NEXT_PUBLIC_TAG_NAME ??
          "local-dev",
      },
      loggerSettings: {
        ...loggerSettings,
        batchMessages: true,
        captureClick: false,
        captureError: true,
        captureHrefChange: true,
        capturePageLoad: false,
        enableConsoleLogs: false,
      },
    });

    resolveLoad();
  };
export const logError = async (data: unknown) => {
  await loaded;
  getLogOtherEvent()?.("error", data);
};
export const logData = async (data: unknown, actionType?: string) => {
  await loaded;
  getLogOtherEvent()?.(actionType ?? "event", data);
};
export const update = async (data: Partial<FeLoggerConfig>) => {
  await loaded;
  getAddAdditionalData()?.(data);
};
